import {
  type Contact,
  type ContactLogs,
  type ContactMessage,
  type ContactPersona,
  type EventLogItem,
  type IntegrationName,
  type Integrations,
  type Invoice,
  type InvoiceStatus,
  type MessageChannel,
  type SenderChannelStatus,
  type SenderStatusPayload,
  type Tenant,
  type SubscriptionSummary,
  type SubscriptionCheckoutSession,
  type SubscriptionDefaults,
  type SubscriptionCardSummary,
  type SubscriptionNotificationEvent,
  type BillingUsageSummary,
  type StripePlanCatalog,
  type OnboardingBootstrapResponse,
  type OnboardingPrestartResponse,
  type DemoThreadPayload,
  type DemoThreadMessage,
} from "../types"
import { apiFetch, ApiError } from "./http"
import rawStripePlanCatalog from "../data/stripe_plans.json" assert { type: "json" }

const baseSmsStatus: SenderChannelStatus = {
  status: "ready",
  sender_identifier: "+15551235555",
  messaging_service_sid: "MGbase00000000000000000000000001",
  pending_actions: [],
  metadata: { region: "US" },
}

const baseWhatsappStatus: SenderChannelStatus = {
  status: "pending",
  sender_identifier: "whatsapp:+15551235555",
  pending_actions: ["awaiting_whatsapp_approval"],
  metadata: { region: "US" },
}

const baseSenderStatus: SenderStatusPayload = {
  tenant_id: 1,
  business_profile: {
    region: "US",
    status: "verified",
    external_id: "BP000001US",
  },
  sms: baseSmsStatus,
  whatsapp: baseWhatsappStatus,
}

const stripePlanCatalogRaw = rawStripePlanCatalog as StripePlanCatalog

const stripePlanCatalog: StripePlanCatalog = {
  plans: stripePlanCatalogRaw.plans.map((plan) => ({
    ...plan,
    metadata: plan.metadata ? { ...plan.metadata } : undefined,
  })),
  addons: stripePlanCatalogRaw.addons.map((addon) => ({ ...addon })),
}

let demoSenderStatus: SenderStatusPayload = {
  tenant_id: 2,
  business_profile: {
    region: "US",
    status: "verified",
    external_id: "BP000002US",
  },
  sms: {
    status: "ready",
    sender_identifier: "+15559871234",
    messaging_service_sid: "MGdemo0000000000000000000000001",
    pending_actions: [],
    metadata: { region: "US" },
  },
  whatsapp: {
    status: "ready",
    sender_identifier: "whatsapp:+15559871234",
    pending_actions: [],
    metadata: { region: "US" },
  },
}

let demoOnboardingState: {
  preUserId: string
  firstName: string
  lastName: string
  businessName: string
  email: string
  phone: string
  fakeNumber: string
} = {
  preUserId: "demo-pre-user",
  firstName: "Jamie",
  lastName: "Summoner",
  businessName: "Demo Coven Holdings",
  email: "jamie@example.com",
  phone: "+15559871234",
  fakeNumber: "+1 (555) 210-8ᚠᛉ",
}


const baseTenant: Tenant = {
  name: "Factory/Zen Studio",
  number_e164: baseSmsStatus.sender_identifier ?? "+15551235555",
  payments_ready: false,
  sms_ready: (baseSmsStatus.status ?? "").toLowerCase() === "ready",
  whatsapp_ready: (baseWhatsappStatus.status ?? "").toLowerCase() === "ready",
  allow_web_send: false,
  stripe_webhook: "issue",
  twilio_webhook: "ok",
  stripe_subscription_id: null,
  stripe_sms_item_id: null,
  stripe_plan_id: null,
  support_number: "+15559870000",
  brand_logo_url: "https://example.com/logo.png",
  preferences: {},
  sender_status: baseSenderStatus,
}

const demoTenantTemplate: Tenant = {
  name: "Demo Coven Holdings",
  number_e164: "+15559871234",
  payments_ready: true,
  sms_ready: true,
  whatsapp_ready: true,
  allow_web_send: true,
  stripe_webhook: "ok",
  twilio_webhook: "ok",
  demo: true,
  stripe_subscription_id: "sub_demo_123",
  stripe_sms_item_id: "si_demo_sms",
  stripe_plan_id: "pro",
  support_number: "+15550123456",
  brand_logo_url: "https://example.com/demo-logo.png",
  preferences: { notifications: { nightly_report: true } },
  sender_status: demoSenderStatus,
}

let demoPlanSlug: string = demoTenantTemplate.stripe_plan_id ?? "pro"
let demoAddonSlugs: string[] = []

const baseInvoices: Invoice[] = [
  {
    id: "inv-001",
    status: "sent",
    amount_cents: 120000,
    customer: "Gary Yoga",
    date_iso: "2025-10-01",
    provider_id: "INV-001",
  },
  {
    id: "inv-002",
    status: "paid",
    amount_cents: 86000,
    customer: "Ash Lantern",
    date_iso: "2025-09-28",
    provider_id: "INV-002",
  },
  {
    id: "inv-003",
    status: "overdue",
    amount_cents: 45000,
    customer: "Northwind Labs",
    date_iso: "2025-09-15",
    provider_id: "INV-003",
  },
]

const demoInvoices: Invoice[] = [
  ...baseInvoices,
  {
    id: "inv-004",
    status: "sent",
    amount_cents: 132500,
    customer: "Jamie & Co",
    date_iso: "2025-10-12",
    provider_id: "INV-004",
  },
]

const baseContacts: Contact[] = [
  {
    id: "ct-1",
    name: "Gary Yoga",
    demon_id: "DMN-4421",
    status: "active",
    chat_count: 18,
    balance_cents: 3000,
    persona: "demon",
    last_message_at: "2025-10-20T16:03:10Z",
    latest_invoice_id: "inv-001",
  },
  {
    id: "ct-2",
    name: "Ash Lantern",
    demon_id: "DMN-1550",
    status: "fairy",
    chat_count: 6,
    balance_cents: 0,
    persona: "fairy",
    last_message_at: "2025-10-18T12:45:10Z",
  },
  {
    id: "ct-3",
    name: "Northwind Labs",
    demon_id: "DMN-3382",
    status: "overdue",
    chat_count: 34,
    balance_cents: 45000,
    persona: "demon",
    last_message_at: "2025-10-22T09:13:20Z",
  },
]

const demoContacts: Contact[] = [
  ...baseContacts,
  {
    id: "ct-4",
    name: "Orchid Rail",
    demon_id: "DMN-7712",
    status: "awaiting",
    chat_count: 4,
    balance_cents: 12500,
    persona: "demon",
    last_message_at: "2025-10-23T18:40:09Z",
  },
]

const ADDITIONAL_DEMO_ENTRIES = 50
const contactStatusCycle: Contact["status"][] = ["active", "overdue", "awaiting", "fairy"]
const invoiceStatusCycle: InvoiceStatus[] = ["sent", "paid", "overdue"]
const additionalBaseDate = Date.parse("2025-08-25T12:00:00Z")

for (let i = 0; i < ADDITIONAL_DEMO_ENTRIES; i += 1) {
  const contactNumber = 5 + i
  const contactId = `ct-${contactNumber}`
  const invoiceId = `inv-${String(contactNumber).padStart(3, "0")}`
  const status = contactStatusCycle[i % contactStatusCycle.length]
  const persona: ContactPersona = status === "fairy" ? "fairy" : "demon"
  const amountSeed = 7500 + (i % 12) * 3250
  const lastMessageDate = new Date(additionalBaseDate - i * 86400000).toISOString()

  demoContacts.push({
    id: contactId,
    name: `Demo Customer ${String(contactNumber).padStart(2, "0")}`,
    demon_id: `DMN-${(6700 + contactNumber * 7).toString().padStart(4, "0")}`,
    status,
    chat_count: (i * 3) % 24 + 3,
    balance_cents: amountSeed % 2 === 0 ? 0 : amountSeed,
    persona,
    last_message_at: lastMessageDate,
    latest_invoice_id: invoiceId,
  })

  const invoiceStatus = invoiceStatusCycle[i % invoiceStatusCycle.length]
  const invoiceDate = new Date(additionalBaseDate - i * 86400000).toISOString().slice(0, 10)

  demoInvoices.push({
    id: invoiceId,
    status: invoiceStatus,
    amount_cents: amountSeed,
    customer: `Demo Customer ${String(contactNumber).padStart(2, "0")}`,
    date_iso: invoiceDate,
    provider_id: `INV-${String(contactNumber).padStart(3, "0")}`,
  })
}

const contactLogs: Record<string, ContactLogs> = {
  "ct-1": {
    chat: [
      { at: "2025-10-20T16:03:00Z", dir: "in", body: "Invoice Gary for $3k @ gary@yoga.com" },
      { at: "2025-10-20T16:03:08Z", dir: "out", body: "Summoned pay link DEMN-INV-778. Sent PDF + SMS." },
      { at: "2025-10-20T16:03:10Z", dir: "out", body: "Let me know when he pays and I'll archive." },
    ],
    ops: [
      { at: "2025-10-20T16:03:02Z", kind: "parse", detail: "muttered a parse of contact from text" },
      { at: "2025-10-20T16:03:05Z", kind: "stripe", detail: "etched Stripe customer" },
      { at: "2025-10-20T16:03:06Z", kind: "invoice", detail: "forged deposit invoice INV-778" },
      { at: "2025-10-20T16:04:16Z", kind: "webhook", detail: "webhook omen: payment succeeded" },
    ],
    reasoning: [
      {
        at: "2025-10-20T16:03:04Z",
        label: "Private note",
        persona: "demon",
        summary: "Gary amount validated via parser + ledger",
        confidence_label: "medium",
        confidence_score: 0.64,
        sources: ["invoice-parser", "ledger-history"],
        metadata: {
          fields: ["amount", "email"],
        },
      },
    ],
  },
  "ct-2": {
    chat: [
      { at: "2025-10-18T12:45:00Z", dir: "in", body: "Switch Ash to fairy guidance." },
      { at: "2025-10-18T12:45:10Z", dir: "out", body: "Fairy whispers engaged. Gentle follow-ups scheduled." },
    ],
    ops: [
      { at: "2025-10-18T12:45:04Z", kind: "persona", detail: "whispers tuned to Fairy Garden cadence" },
    ],
    reasoning: [
      {
        at: "2025-10-18T12:45:06Z",
        label: "Persona Shift",
        persona: "fairy",
        summary: "Persona toggled to Fairy for overdue soft touch",
        confidence_label: "high",
        confidence_score: 0.91,
        sources: ["operator-toggle"],
      },
    ],
  },
  "ct-3": {
    chat: [
      { at: "2025-10-22T09:13:00Z", dir: "in", body: "Collect from all overdue >30 days" },
      { at: "2025-10-22T09:13:20Z", dir: "out", body: "Summoner commands despatched. Reports will arrive in 2h." },
    ],
    ops: [
      { at: "2025-10-22T09:13:05Z", kind: "scan", detail: "muttered a parse of 7 overdue ledgers" },
      { at: "2025-10-22T09:13:24Z", kind: "reminder", detail: "sent pay link via email & SMS" },
      { at: "2025-10-22T09:13:30Z", kind: "schedule", detail: "scheduled balance in 30 days" },
    ],
    reasoning: [
      {
        at: "2025-10-22T09:13:34Z",
        label: "Bulk Reminder Confidence",
        persona: "demon",
        summary: "High confidence bulk reminders with debt history weighting",
        confidence_label: "medium",
        confidence_score: 0.72,
        sources: ["overdue-ledger", "pay-link-tracker"],
      },
    ],
  },
  "ct-4": {
    chat: [
      { at: "2025-10-23T18:40:00Z", dir: "in", body: "Add Orchid to demo loop." },
      { at: "2025-10-23T18:40:09Z", dir: "out", body: "Staged introduction message. Waiting for their first reply." },
    ],
    ops: [
      { at: "2025-10-23T18:40:03Z", kind: "prepare", detail: "etched welcome kit for Orchid Rail" },
    ],
    reasoning: [
      {
        at: "2025-10-23T18:40:05Z",
        label: "Onboarding",
        persona: "demon",
        summary: "Prepared welcome kit; awaiting contact confirmation",
        confidence_label: "low",
        confidence_score: 0.38,
        sources: ["demo-seed"],
      },
    ],
  },
}

type DemoGhostNote = {
  message_id: number
  summary: string
  ghost_note: Record<string, unknown>
  confidence_label?: string | null
  confidence_score?: number | null
  confidence_sources: string[]
  created_at: string
}

const demoGhostNotes: Record<string, DemoGhostNote[]> = {}

type DemoNotificationChannel = {
  digest_enabled: boolean
  quiet_hours: Record<string, string | null> | null
  updated_at: string
}

const demoNotificationPreferences: {
  weekly_digest: boolean
  weekly_digest_updated_at: string | null
  channels: Record<string, DemoNotificationChannel>
} = {
  weekly_digest: false,
  weekly_digest_updated_at: null,
  channels: {
    sms: { digest_enabled: false, quiet_hours: null, updated_at: new Date().toISOString() },
    email: { digest_enabled: false, quiet_hours: null, updated_at: new Date().toISOString() },
    web: { digest_enabled: false, quiet_hours: null, updated_at: new Date().toISOString() },
  },
}

const contactMessageStore: Record<string, ContactMessage[]> = {}
let contactMessageSequence = 2000

function ensureContactMessages(contactId: string) {
  if (contactMessageStore[contactId]) return contactMessageStore[contactId]
  const logs = contactLogs[contactId]
  const seeded: ContactMessage[] = logs
    ? logs.chat.map((entry, index) => {
        contactMessageSequence += 1
        return {
          id: contactMessageSequence,
          contact_id: Number(contactId.replace(/\D/g, "")) || index + 1,
          tenant_id: 1,
          direction: entry.dir === "in" ? "inbound" : "outbound",
          origin: entry.dir === "in" ? "sms" : "sms",
          channel: "sms",
          body: entry.body,
          metadata: {},
          delivery_status: entry.dir === "out" ? "delivered" : "received",
          request_id: entry.dir === "out" ? `demo-req-${contactMessageSequence}` : null,
          ghost_note: null,
          confidence_label: null,
          confidence_score: null,
          confidence_sources: null,
          created_at: entry.at,
        }
      })
    : []

  if (logs && !demoGhostNotes[contactId]) {
    const notes: DemoGhostNote[] = []
    const inboundMessages = seeded.filter((message) => message.direction === "inbound")

    if (contactId === "ct-1" && inboundMessages[0]) {
      const target = inboundMessages[0]
      const summary = "Double-check $3k amount for Gary"
      target.ghost_note = { summary, fields: ["amount", "email"] }
      target.confidence_label = "medium"
      target.confidence_score = 0.64
      target.confidence_sources = ["invoice-parser", "ledger-history"]
      notes.push({
        message_id: target.id,
        summary,
        ghost_note: target.ghost_note,
        confidence_label: target.confidence_label,
        confidence_score: target.confidence_score,
        confidence_sources: target.confidence_sources ?? [],
        created_at: target.created_at,
      })
      if (inboundMessages[1]) {
        const follow = inboundMessages[1]
        const followSummary = "Low-confidence email typo detected"
        follow.ghost_note = { summary: followSummary, fields: ["email"] }
        follow.confidence_label = "low"
        follow.confidence_score = 0.32
        follow.confidence_sources = ["pattern-heuristics"]
        notes.push({
          message_id: follow.id,
          summary: followSummary,
          ghost_note: follow.ghost_note,
          confidence_label: follow.confidence_label,
          confidence_score: follow.confidence_score,
          confidence_sources: follow.confidence_sources ?? [],
          created_at: follow.created_at,
        })
      }
    }

    if (contactId === "ct-3" && inboundMessages[0]) {
      const target = inboundMessages[0]
      const summary = "Bulk overdue sweep triggered"
      target.ghost_note = { summary, fields: ["overdue_contacts"] }
      target.confidence_label = "medium"
      target.confidence_score = 0.72
      target.confidence_sources = ["overdue-ledger", "pay-link-tracker"]
      notes.push({
        message_id: target.id,
        summary,
        ghost_note: target.ghost_note,
        confidence_label: target.confidence_label,
        confidence_score: target.confidence_score,
        confidence_sources: target.confidence_sources ?? [],
        created_at: target.created_at,
      })
      if (inboundMessages[1]) {
        const follow = inboundMessages[1]
        const followSummary = "High confidence payment reminder ready"
        follow.ghost_note = { summary: followSummary, fields: ["reminder"] }
        follow.confidence_label = "high"
        follow.confidence_score = 0.9
        follow.confidence_sources = ["reminder-engine", "payer-history"]
        notes.push({
          message_id: follow.id,
          summary: followSummary,
          ghost_note: follow.ghost_note,
          confidence_label: follow.confidence_label,
          confidence_score: follow.confidence_score,
          confidence_sources: follow.confidence_sources ?? [],
          created_at: follow.created_at,
        })
      }
    }

    if (notes.length) {
      demoGhostNotes[contactId] = notes
    }
  }

  if (contactId === "ct-1") {
    const createdAt = new Date(Date.now() - 1000 * 60 * 42).toISOString()
    contactMessageSequence += 1
    seeded.push({
      id: contactMessageSequence,
      contact_id: Number(contactId.replace(/\D/g, "")) || contactMessageSequence,
      tenant_id: 1,
      direction: "inbound",
      origin: "sms",
      channel: "sms",
      body: "Invoice ready: confirm to send $3,200 to jane@acme.com?",
      metadata: {
        intent_confidence: {
          score: 0.41,
          label: "low",
          reasons: ["missing_email", "manual_confirm"],
          sources: ["demo-intent"],
          evaluated_at: createdAt,
          requires_confirm: true,
          degraded: true,
        },
      },
      delivery_status: "received",
      request_id: null,
      ghost_note: null,
      confidence_label: null,
      confidence_score: null,
      confidence_sources: null,
      created_at: createdAt,
    })
  }

  contactMessageStore[contactId] = seeded
  return contactMessageStore[contactId]
}

const baseIntegrations: Integrations = {
  stripe: "missing",
  xero: "missing",
  quickbooks: "missing",
  wave: "missing",
}

const demoIntegrations: Integrations = {
  ...baseIntegrations,
  stripe: "connected",
}

const baseEvents: EventLogItem[] = [
  {
    id: "evt-1",
    at: "2025-10-24T10:02:00Z",
    category: "invoice",
    summary: "Invoice INV-778 forged for Gary Yoga",
    detail: "$3,000 balance now awaiting signature.",
  },
  {
    id: "evt-2",
    at: "2025-10-24T12:14:00Z",
    category: "payment",
    summary: "Stripe webhook failure",
    detail: "Retry queue engaged. Tap beacon to view logs.",
  },
  {
    id: "evt-3",
    at: "2025-10-23T20:15:00Z",
    category: "reminder",
    summary: "Twilio reminder to Northwind Labs",
    detail: "Overdue 21 days. Demon awaiting response.",
  },
]

const demoEvents: EventLogItem[] = [
  ...baseEvents,
  {
    id: "evt-4",
    at: "2025-10-25T07:05:00Z",
    category: "system",
    summary: "Demo state prepared",
    detail: "Demo mode — nothing is sent to real customers.",
  },
]

type DemoSubscriptionRecord = {
  id: string
  contact_id: string | null
  contact_name?: string | null
  status: string
  cadence: string
  amount_cents: number
  currency: string
  cancel_at_period_end: boolean
  current_period_end: string | null
  stripe_subscription_id: string | null
  checkout_session_id?: string | null
  checkout_url?: string | null
  description?: string | null
  created_at: string
  updated_at: string
  metadata?: Record<string, unknown>
}

const defaultSubscriptionDefaults: SubscriptionDefaults = {
  cadence: "month",
  amount_cents: 4900,
  currency: "USD",
}

function buildInitialDemoSubscriptionRecords(): DemoSubscriptionRecord[] {
  const now = new Date()
  return [
    {
      id: "sub_demo",
      contact_id: "ct-1",
      contact_name: resolveContactName("ct-1"),
      status: "active",
      cadence: "month",
      amount_cents: 4900,
      currency: "USD",
      cancel_at_period_end: false,
      current_period_end: new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000).toISOString(),
      stripe_subscription_id: "sub_demo",
      checkout_session_id: null,
      checkout_url: null,
      description: "Monthly retainer",
      created_at: new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000).toISOString(),
      updated_at: now.toISOString(),
      metadata: {
        checkout_session_id: "cs_demo_active",
        email: "gary@yoga.com",
      },
    },
  ]
}

let demoSubscriptionRecords: DemoSubscriptionRecord[] = buildInitialDemoSubscriptionRecords()
let demoLastSubscriptionContactId: string | null = demoSubscriptionRecords[0]?.contact_id ?? null

const demoCheckoutSeed: SubscriptionCheckoutSession = {
  id: "cs_demo_active",
  url: "https://billing.stripe.com/p/test_demo",
  status: "open",
  expires_at: Math.floor(Date.now() / 1000) + 1800,
}

const demoSubscriptionNotifications: SubscriptionNotificationEvent[] = [
  {
    id: "evt_demo_subscription_reminder",
    type: "renewal_reminder",
    channel: "email",
    status: "recorded",
    contact_id: demoSubscriptionRecords[0]?.contact_id ?? null,
    contact_name: resolveContactName(demoSubscriptionRecords[0]?.contact_id ?? null),
    contact_email: "gary@yoga.com",
    sent_at: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    metadata: {
      amount_cents: demoSubscriptionRecords[0]?.amount_cents ?? defaultSubscriptionDefaults.amount_cents,
      cadence: demoSubscriptionRecords[0]?.cadence ?? defaultSubscriptionDefaults.cadence,
    },
  },
  {
    id: "evt_demo_subscription_digest",
    type: "digest_summary",
    channel: "email",
    status: "recorded",
    contact_id: null,
    contact_name: null,
    contact_email: null,
    sent_at: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
    metadata: {
      active_count: demoSubscriptionRecords.length,
      total_count: demoSubscriptionRecords.length,
    },
  },
]

const demoSubscription: SubscriptionSummary & {
  cards: SubscriptionCardSummary[]
} = {
  status: "active",
  plan: {
    name: "Monthly plan",
    price_cents: 4900,
    currency: "USD",
    interval: "month",
    cancel_at_period_end: false,
    current_period_end: demoSubscriptionRecords[0].current_period_end,
    stripe_subscription_id: "sub_demo",
    defaults: defaultSubscriptionDefaults,
  },
  usage: {
    invoices_last_30_days: 14,
  },
  portal_url: demoCheckoutSeed.url,
  cards: [
    {
      id: "plan",
      title: "Monthly plan",
      status: "active",
      amount_cents: 4900,
      currency: "USD",
      interval: "month",
      cancel_at_period_end: false,
      current_period_end: demoSubscriptionRecords[0].current_period_end,
      defaults: defaultSubscriptionDefaults,
    },
    {
      id: "weekly_digest",
      title: "Weekly Digest",
      status: demoNotificationPreferences.weekly_digest ? "enabled" : "disabled",
      updated_at: demoNotificationPreferences.weekly_digest_updated_at,
      cta: "Manage notifications",
      cta_action: "open_notifications",
    },
  ],
  records: demoSubscriptionRecords.map((record) => ({
    id: record.id,
    contact_id: record.contact_id,
    contact_name: resolveContactName(record.contact_id),
    status: record.status,
    cadence: record.cadence,
    amount_cents: record.amount_cents,
    currency: record.currency,
    cancel_at_period_end: record.cancel_at_period_end,
    current_period_end: record.current_period_end,
    stripe_subscription_id: record.stripe_subscription_id,
    checkout_session_id: record.checkout_session_id ?? null,
    checkout_url: record.checkout_url ?? null,
    description: record.description ?? null,
    defaults: {
      cadence: record.cadence,
      amount_cents: record.amount_cents,
      currency: record.currency,
    },
    updated_at: record.updated_at,
    created_at: record.created_at,
  })),
  checkout_session: demoCheckoutSeed,
  primary_contact_id: demoSubscriptionRecords[0].contact_id,
  notifications: demoSubscriptionNotifications,
}

function normalizeSubscriptionDefaults(record: DemoSubscriptionRecord | null | undefined): SubscriptionDefaults {
  return {
    cadence: record?.cadence ?? defaultSubscriptionDefaults.cadence ?? "month",
    amount_cents: record?.amount_cents ?? defaultSubscriptionDefaults.amount_cents ?? null,
    currency: record?.currency ?? defaultSubscriptionDefaults.currency ?? "USD",
  }
}

function latestDemoSubscriptionRecord(): DemoSubscriptionRecord | null {
  if (!demoSubscriptionRecords.length) {
    return null
  }
  const sorted = [...demoSubscriptionRecords].sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
  return sorted.find((record) => record.status !== "canceled") ?? sorted[0] ?? null
}

function latestDemoSubscriptionRecordForContact(contactId: string | null | undefined): DemoSubscriptionRecord | null {
  if (!contactId) {
    return null
  }
  const matches = demoSubscriptionRecords.filter((record) => record.contact_id === contactId)
  if (!matches.length) {
    return null
  }
  return [...matches].sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())[0] ?? null
}

function syncDemoSubscriptionSummary() {
  const current = latestDemoSubscriptionRecord()
  const defaults = normalizeSubscriptionDefaults(current)

  demoSubscription.status = current?.status ?? "none"
  demoSubscription.plan = current
    ? {
        name: current.description ?? "Monthly plan",
        price_cents: current.amount_cents,
        currency: current.currency,
        interval: current.cadence,
        cancel_at_period_end: current.cancel_at_period_end,
        current_period_end: current.current_period_end,
        stripe_subscription_id: current.stripe_subscription_id,
        defaults,
      }
    : null

  demoSubscription.cards = [
    {
      id: "plan",
      title: current?.description ?? "Monthly plan",
      status: current?.status ?? "none",
      amount_cents: current?.amount_cents ?? null,
      currency: current?.currency ?? defaults.currency,
      interval: current?.cadence ?? defaults.cadence,
      cancel_at_period_end: current?.cancel_at_period_end ?? false,
      current_period_end: current?.current_period_end ?? null,
      defaults,
      checkout_session_id: current?.checkout_session_id ?? null,
      checkout_url: current?.checkout_url ?? null,
    },
    {
      id: "weekly_digest",
      title: "Weekly Digest",
      status: demoNotificationPreferences.weekly_digest ? "enabled" : "disabled",
      updated_at: demoNotificationPreferences.weekly_digest_updated_at,
      cta: "Manage notifications",
      cta_action: "open_notifications",
    },
  ]

  demoSubscription.records = demoSubscriptionRecords.map((record) => ({
    id: record.id,
    contact_id: record.contact_id,
    contact_name: resolveContactName(record.contact_id),
    status: record.status,
    cadence: record.cadence,
    amount_cents: record.amount_cents,
    currency: record.currency,
    cancel_at_period_end: record.cancel_at_period_end,
    current_period_end: record.current_period_end,
    stripe_subscription_id: record.stripe_subscription_id,
    checkout_session_id: record.checkout_session_id ?? null,
    checkout_url: record.checkout_url ?? null,
    description: record.description ?? null,
    defaults: {
      cadence: record.cadence,
      amount_cents: record.amount_cents,
      currency: record.currency,
    },
    metadata: record.metadata ?? null,
    updated_at: record.updated_at,
    created_at: record.created_at,
  }))
  if (demoSubscriptionNotifications.length) {
    const primaryContactId = current?.contact_id ?? demoSubscriptionRecords[0]?.contact_id ?? null
    const reminder = demoSubscriptionNotifications[0]
    reminder.contact_id = primaryContactId
    reminder.contact_name = resolveContactName(primaryContactId)
    const reminderMetadata = (reminder.metadata ?? {}) as Record<string, unknown>
    reminderMetadata.amount_cents = current?.amount_cents ?? defaultSubscriptionDefaults.amount_cents
    reminderMetadata.cadence = current?.cadence ?? defaultSubscriptionDefaults.cadence
    reminderMetadata.email = reminderMetadata.email ?? "gary@yoga.com"
    reminder.metadata = reminderMetadata
  }
  if (demoSubscriptionNotifications.length > 1) {
    const digest = demoSubscriptionNotifications[1]
    const digestMetadata = (digest.metadata ?? {}) as Record<string, unknown>
    digestMetadata.active_count = demoSubscriptionRecords.filter((entry) => entry.status !== "canceled").length
    digestMetadata.total_count = demoSubscriptionRecords.length
    digest.metadata = digestMetadata
  }
  demoSubscription.notifications = demoSubscriptionNotifications.map((entry) => ({
    ...entry,
    contact_name: entry.contact_id ? resolveContactName(entry.contact_id) : entry.contact_name,
  }))

  const pendingCheckout = demoSubscriptionRecords.find((record) => record.status === "pending" && record.checkout_session_id)
  demoSubscription.portal_url = current?.stripe_subscription_id ? demoCheckoutSeed.url : pendingCheckout?.checkout_url ?? demoCheckoutSeed.url
  demoSubscription.checkout_session = pendingCheckout
    ? {
        id: pendingCheckout.checkout_session_id ?? `${pendingCheckout.id}_checkout`,
        url: pendingCheckout.checkout_url ?? demoCheckoutSeed.url,
        status: pendingCheckout.status,
        expires_at: Math.floor(Date.now() / 1000) + 1800,
      }
    : null

  demoSubscription.primary_contact_id = current?.contact_id ?? null
}

syncDemoSubscriptionSummary()

function parseContactNumericId(raw: string | null | undefined): number | null {
  if (!raw) return null
  if (/^\d+$/.test(raw)) {
    const parsed = Number(raw)
    return Number.isNaN(parsed) ? null : parsed
  }
  const digits = raw.replace(/\D/g, "")
  if (!digits) return null
  const parsed = Number(digits)
  return Number.isNaN(parsed) ? null : parsed
}

function resolveContactName(contactId: string | null | undefined): string {
  if (!contactId) return "Subscription"
  const match = demoContacts.find((entry) => entry.id === contactId)
  return match ? match.name : `Contact ${contactId}`
}

function syncDemoDigestCard() {
  const card = demoSubscription.cards?.find((entry) => entry.id === "weekly_digest")
  if (!card) return
  card.status = demoNotificationPreferences.weekly_digest ? "enabled" : "disabled"
  card.updated_at = demoNotificationPreferences.weekly_digest_updated_at
}

let demoQuietHoursState: {
  enabled: boolean
  start_time: string | null
  end_time: string | null
  timezone: string
} = {
  enabled: true,
  start_time: "20:00",
  end_time: "07:00",
  timezone: "US/Pacific",
}

const demoDeadLetters = [
  {
    id: 1,
    schedule_id: 42,
    job_type: "balance_follow_up",
    error_message: "Demo timeout",
    created_at: new Date().toISOString(),
    requeued_count: 0,
    last_requeued_at: null,
    contact: {
      id: 10,
      display_name: "Demo Contact",
      email: "demo@example.com",
      phone_number: "+***0100",
      persona: "demon",
    },
    tenant: {
      id: 1,
      name: "Demo Coven Holdings",
    },
    payload: {
      correlation_id: "demo-correlation",
      tenant_id: 1,
      contact_id: 10,
    },
  },
]

const demoWebhookEvents = [
  {
    id: 12,
    event_id: "evt_demo_1",
    type: "invoice.payment_succeeded",
    processed_at: new Date().toISOString(),
    object_id: "in_demo_1",
    connected_account: "acct_demo",
  },
]

const demoAdminTenants = [
  {
    id: 1,
    name: "Demo Coven Holdings",
    stripe_account_id: "acct_demo",
    application_fee_bps: 250,
    default_currency: "USD",
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
]

function clone<T>(value: T): T {
  if (typeof structuredClone === "function") {
    return structuredClone(value)
  }
  return JSON.parse(JSON.stringify(value))
}


function delay<T>(value: T, ms = 220): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(clone(value)), ms))
}

function pickData<T>(demo: boolean, base: T, demoValue?: T): T {
  if (demo && demoValue) return demoValue
  return base
}

function authHeaders(token?: string | null): HeadersInit {
  return token ? { Authorization: `Bearer ${token}` } : {}
}

function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  return apiFetch<T>(path, init)
}

type InvoicePage = { items: Invoice[]; nextCursor: string | null }
type ContactPage = { items: Contact[]; nextCursor: string | null }

type InvoicePayload = {
  id: string
  status: InvoiceStatus
  amount_cents: number
  customer: string
  date_iso: string
  provider_id: string
  contact_id?: string | null
  hosted_invoice_url?: string | null
}

type ContactPayload = {
  id: string
  name: string
  demon_id: string
  status: Contact["status"]
  chat_count: number
  balance_cents: number
  latest_invoice_id?: string | null
  persona?: Contact["persona"]
  last_message_at?: string | null
}

function mapInvoicePayload(raw: InvoicePayload): Invoice {
  return {
    id: raw.id,
    status: raw.status,
    amount_cents: raw.amount_cents,
    customer: raw.customer,
    date_iso: raw.date_iso,
    provider_id: raw.provider_id,
    contact_id: raw.contact_id ?? undefined,
    hosted_invoice_url: raw.hosted_invoice_url ?? null,
  }
}

function mapContactPayload(raw: ContactPayload): Contact {
  return {
    id: raw.id,
    name: raw.name,
    demon_id: raw.demon_id,
    status: raw.status,
    chat_count: raw.chat_count,
    balance_cents: raw.balance_cents,
    latest_invoice_id: raw.latest_invoice_id ?? undefined,
    persona: raw.persona ?? "demon",
    last_message_at: raw.last_message_at ?? null,
  }
}

export const mockClient = {
  async getOnboardingBootstrap(
    params: { preUserId?: string; forceVariant?: string } = {},
  ): Promise<OnboardingBootstrapResponse> {
    if (params.preUserId) {
      demoOnboardingState.preUserId = params.preUserId
    }
    const name = `${demoOnboardingState.firstName} ${demoOnboardingState.lastName}`.trim() || null
    return {
      user: {
        id: 0,
        firstName: demoOnboardingState.firstName || null,
        lastName: demoOnboardingState.lastName || null,
        name,
        email: demoOnboardingState.email,
        phone: demoOnboardingState.phone,
      },
      demon: {
        status: "PENDING_EMAIL",
        realNumber: null,
        mask: demoOnboardingState.fakeNumber,
      },
      preUser: demoOnboardingState.preUserId
        ? {
            id: demoOnboardingState.preUserId,
            firstName: demoOnboardingState.firstName,
            lastName: demoOnboardingState.lastName,
            email: demoOnboardingState.email,
            phone: demoOnboardingState.phone,
            businessName: demoOnboardingState.businessName,
            mask: demoOnboardingState.fakeNumber,
            status: "PENDING_EMAIL",
          }
        : null,
      flags: {
        "onboarding.getDemonNumberV1": true,
        "onboarding.phoneOtp": false,
      },
    }
  },
  async onboardingPrestart(payload: {
    firstName: string
    lastName: string
    businessName?: string | null
    email: string
    phone?: string | null
  }): Promise<OnboardingPrestartResponse> {
    demoOnboardingState = {
      ...demoOnboardingState,
      firstName: payload.firstName,
      lastName: payload.lastName,
      businessName: payload.businessName ?? demoOnboardingState.businessName,
      email: payload.email,
      phone: payload.phone ?? demoOnboardingState.phone,
      fakeNumber: `+1 (555) 210-${Math.floor(Math.random() * 9)}ᚠᛉᛗ`,
    }
    return {
      preUserId: demoOnboardingState.preUserId,
      fakeDemonNumber: demoOnboardingState.fakeNumber,
    }
  },
  async onboardingResend(preUserId: string): Promise<void> {
    void preUserId
  },
  async getOnboardingDemoThread(): Promise<DemoThreadPayload> {
    const now = new Date()
    const day = now.getDay()
    const delta = (5 - day + 7) % 7 || 7
    const target = new Date(now.getFullYear(), now.getMonth(), now.getDate() + delta)
    const formatter = new Intl.DateTimeFormat(undefined, { month: "long", day: "numeric" })
    const nextFriday = formatter.format(target)
    const invoiceLink = "https://example.com/demo-invoice"
    const internal: DemoThreadMessage[] = [
      { speaker: "you", text: "Invoice Jane Carter for $500, due next Friday." },
      {
        speaker: "demon",
        text: `Got it. I’ll create the invoice and message Jane from ${demoOnboardingState.businessName}.`,
      },
      { speaker: "you", text: "Add a friendly note about the project kickoff." },
      { speaker: "demon", text: "Note added. Sending now. I’ll log all replies here." },
      {
        speaker: "demon",
        text: "Jane plans to pay Monday. Want me to send a reminder that morning?",
      },
      { speaker: "you", text: "Yes, 9am." },
      {
        speaker: "demon",
        text: "Reminder scheduled for Monday 9:00 AM. All logged in your timeline.",
      },
    ]
    const client: DemoThreadMessage[] = [
      {
        speaker: "demon",
        text: `Hi Jane, I’m ${demoOnboardingState.firstName}’s assistant from ${demoOnboardingState.businessName}. Here’s your invoice for $500, due ${nextFriday}. Pay here: ${invoiceLink}. If you have any questions, reply to this message.`,
      },
      { speaker: "client", text: "Thanks! Can I pay on Monday?" },
      {
        speaker: "demon",
        text: `Absolutely. I’ll note Monday as your planned payment date and keep ${demoOnboardingState.firstName} in the loop.`,
      },
    ]
    return {
      maskedNumber: demoOnboardingState.fakeNumber,
      nextFridayDate: nextFriday,
      invoiceLink,
      banner: "Demo only — no real messages are being sent.",
      threads: {
        internal,
        client,
      },
      sync: [
        { internalIndex: 0 },
        { internalIndex: 1, clientIndex: 0 },
        { internalIndex: 2 },
        { internalIndex: 3, clientIndex: 0 },
        { clientIndex: 1 },
        { internalIndex: 4, clientIndex: 1 },
        { clientIndex: 2 },
        { internalIndex: 5 },
        { internalIndex: 6 },
      ],
    }
  },
  async getOnboardingStripeLink(): Promise<string> {
    return "https://connect.stripe.com/demo"
  },
  async saveOnboardingA2P(fields: Record<string, unknown>): Promise<void> {
    void fields
  },
  async getBillingPlans(demo: boolean): Promise<StripePlanCatalog> {
    void demo
    return {
      plans: stripePlanCatalog.plans.map((plan) => ({
        ...plan,
        metadata: plan.metadata ? { ...plan.metadata } : undefined,
      })),
      addons: stripePlanCatalog.addons.map((addon) => ({ ...addon })),
    }
  },
  async getBillingUsage(demo: boolean): Promise<BillingUsageSummary> {
    void demo
    const plan =
      stripePlanCatalog.plans.find((entry) => entry.slug === demoPlanSlug) ?? stripePlanCatalog.plans[0]
    const monthToDate = 620
    const last24h = 48
    const percent = plan.included_messages ? monthToDate / plan.included_messages : null
    return {
      plan: {
        ...plan,
        metadata: plan.metadata ? { ...plan.metadata } : undefined,
      },
      usage: {
        last_24h: last24h,
        month_to_date: monthToDate,
        included_messages: plan.included_messages,
        overage_rate: plan.overage_rate,
        percent_of_quota: percent,
      },
      active_addons: [...demoAddonSlugs],
    }
  },
  async updateBillingPlan(planSlug: string): Promise<BillingUsageSummary> {
    demoPlanSlug = planSlug.trim().toLowerCase()
    demoTenantTemplate.stripe_plan_id = demoPlanSlug
    return this.getBillingUsage(true)
  },
  async updateBillingAddons(addonSlugs: string[]): Promise<BillingUsageSummary> {
    const normalized = Array.from(
      new Set(addonSlugs.map((slug) => slug.trim().toLowerCase()).filter((slug) => slug.length > 0)),
    )
    demoAddonSlugs = normalized
    return this.getBillingUsage(true)
  },
  async createBillingPortalSession(): Promise<string> {
    return "https://billing.stripe.com/p/session/demo"
  },
  async getTenant(demo: boolean, token?: string | null): Promise<Tenant> {
    if (demo) {
      const status = clone(demoSenderStatus)
      const smsReady = (status.sms?.status ?? "").toLowerCase() === "ready"
      const whatsappReady = (status.whatsapp?.status ?? "").toLowerCase() === "ready"
      const tenant: Tenant = {
        ...demoTenantTemplate,
        id: 0,
        demo: true,
        number_e164: status.sms?.sender_identifier ?? demoTenantTemplate.number_e164,
        sms_ready: smsReady,
        whatsapp_ready: whatsappReady,
        sender_status: status,
      }
      return delay(tenant, 180)
    }
    try {
      const headers = authHeaders(token)
      const tenant = await request<Tenant>("/api/tenants/me", { headers })
      return {
        ...tenant,
        demo: false,
        preferences: { ...(tenant.preferences ?? {}) },
      }
    } catch (error) {
      console.warn("API request failed:", error)
      // If the tenant is not found (404), return a pending state to trigger onboarding
      if (error instanceof ApiError && error.status === 404) {
        const pendingTenant: Tenant = {
          ...baseTenant,
          id: 0,
          demo: false,
          sms_ready: false,
          whatsapp_ready: false,
          payments_ready: false,
          onboarding_state: "not_started",
          number_e164: "", // Clear the dummy number
          preferences: { ...baseTenant.preferences! },
        }
        return pendingTenant
      }
      // For other errors, rethrow to show the error boundary/screen
      throw error
    }
  },

  async getInvoices(
    demo: boolean,
    params: { status?: InvoiceStatus | ""; query?: string; cursor?: string | null } = {},
    token?: string | null,
  ): Promise<InvoicePage> {
    if (demo) {
      let list = pickData(true, baseInvoices, demoInvoices)
      if (params.status) {
        list = list.filter((item) => item.status === params.status)
      }
      if (params.query) {
        const needle = params.query.toLowerCase()
        list = list.filter((item) =>
          [item.customer, item.provider_id].some((field) => field.toLowerCase().includes(needle)),
        )
      }
      return delay({ items: list, nextCursor: null }, 240)
    }

    const search = new URLSearchParams()
    if (params.status) search.set("status", params.status)
    if (params.query) search.set("query", params.query)
    if (params.cursor) search.set("cursor", params.cursor)

    const headers = authHeaders(token)

    const payload = await request<{ items: InvoicePayload[]; next_cursor: string | null }>(
      `/api/invoices${search.toString() ? `?${search}` : ""}`,
      { headers },
    )
    return {
      items: payload.items.map(mapInvoicePayload),
      nextCursor: payload.next_cursor,
    }
  },

  async getContacts(
    demo: boolean,
    params: { query?: string; cursor?: string | null } = {},
    token?: string | null,
  ): Promise<ContactPage> {
    if (demo) {
      let list = pickData(true, baseContacts, demoContacts)
      if (params.query) {
        const needle = params.query.toLowerCase()
        list = list.filter((contact) =>
          [contact.name, contact.demon_id].some((field) => field.toLowerCase().includes(needle)),
        )
      }
      return delay({ items: list, nextCursor: null }, 240)
    }

    const search = new URLSearchParams()
    if (params.query) search.set("query", params.query)
    if (params.cursor) search.set("cursor", params.cursor)

    const headers = authHeaders(token)

    const payload = await request<{ items: ContactPayload[]; next_cursor: string | null }>(
      `/api/contacts${search.toString() ? `?${search}` : ""}`,
      { headers },
    )
    return {
      items: payload.items.map(mapContactPayload),
      nextCursor: payload.next_cursor,
    }
  },

  async getContactLogs(demo: boolean, contactId: string, token?: string | null): Promise<ContactLogs> {
    if (demo) {
      const data = contactLogs[contactId]
      if (!data) {
        return delay(
          {
            chat: [
              { at: new Date().toISOString(), dir: "out", body: "No transcripts yet. Text the demon to begin." },
            ],
            ops: [],
          },
          200,
        )
      }
      return delay(data, 200)
    }
    const headers = authHeaders(token)
    return request<ContactLogs>(`/api/contacts/${contactId}/logs`, { headers })
  },

  async getContactNotes(demo: boolean, contactId: string, token?: string | null) {
    if (demo) {
      ensureContactMessages(contactId)
      const notes = demoGhostNotes[contactId] ?? []
      return delay({ ghost_notes: notes }, 160)
    }
    const headers = authHeaders(token)
    return request(`/api/contacts/${contactId}/notes`, { headers })
  },

  async saveContactNote(
    demo: boolean,
    contactId: string,
    payload: {
      message_id: number
      summary: string
      confidence_label?: string | null
      confidence_score?: number | null
      sources?: string[]
      details?: Record<string, unknown>
    },
    token?: string | null,
  ) {
    if (demo) {
      const store = (demoGhostNotes[contactId] ??= [])
      const now = new Date().toISOString()
      const note: DemoGhostNote = {
        message_id: payload.message_id,
        summary: payload.summary,
        ghost_note: {
          summary: payload.summary,
          ...(payload.details ? { details: payload.details } : {}),
        },
        confidence_label: payload.confidence_label ?? null,
        confidence_score: payload.confidence_score ?? null,
        confidence_sources: payload.sources ?? [],
        created_at: now,
      }
      const existingIndex = store.findIndex((entry) => entry.message_id === payload.message_id)
      if (existingIndex >= 0) {
        store[existingIndex] = note
      } else {
        store.push(note)
      }

      const messages = ensureContactMessages(contactId)
      const target = messages.find((message) => message.id === payload.message_id)
      if (target) {
        target.ghost_note = note.ghost_note
        target.confidence_label = note.confidence_label ?? null
        target.confidence_score = note.confidence_score ?? null
        target.confidence_sources = note.confidence_sources ?? []
      }

      return delay({ ghost_note: note }, 120)
    }

    const headers = authHeaders(token)
    return request(`/api/contacts/${contactId}/notes`, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    })
  },

  async getContactMessages(
    demo: boolean,
    contactId: string,
    params: { since?: string | null } = {},
    token?: string | null,
  ): Promise<{ messages: ContactMessage[] }> {
    if (demo) {
      const messages = ensureContactMessages(contactId)
      const filtered = params.since
        ? messages.filter((item) => new Date(item.created_at).getTime() >= new Date(params.since!).getTime())
        : messages
      return delay({ messages: filtered }, 160)
    }

    const search = new URLSearchParams()
    if (params.since) search.set("since", params.since)
    const headers = authHeaders(token)
    return request<{ messages: ContactMessage[] }>(
      `/api/contacts/${contactId}/messages${search.toString() ? `?${search}` : ""}`,
      { headers },
    )
  },

  async sendContactMessage(
    demo: boolean,
    contactId: string,
    payload: { body: string; channel?: MessageChannel; metadata?: Record<string, unknown> },
    token?: string | null,
  ): Promise<{ message: ContactMessage }> {
    if (demo) {
      const now = new Date().toISOString()
      const channel = payload.channel ?? "sms"
      contactMessageSequence += 1
      const message: ContactMessage = {
        id: contactMessageSequence,
        contact_id: Number(contactId.replace(/\D/g, "")) || contactMessageSequence,
        tenant_id: 1,
        direction: "outbound",
        origin: "web",
        channel,
        body: payload.body,
        metadata: payload.metadata ?? {},
        delivery_status: "queued",
        request_id: `demo-web-${contactMessageSequence}`,
        ghost_note: null,
        confidence_label: null,
        confidence_score: null,
        confidence_sources: null,
        created_at: now,
      }
      const store = ensureContactMessages(contactId)
      store.push(message)
      const contact = demoContacts.find((entry) => entry.id === contactId)
      if (contact) {
        contact.last_message_at = now
        contact.chat_count += 1
      }
      setTimeout(() => {
        message.delivery_status = "delivered"
      }, 400)
      return delay({ message }, 120)
    }

    const headers = authHeaders(token)
    return request(`/api/contacts/${contactId}/messages`, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    })
  },

  async getIntegrations(demo: boolean): Promise<Integrations> {
    const integrations = pickData(demo, baseIntegrations, demoIntegrations)
    return delay(integrations, 150)
  },

  async getEventLog(demo: boolean, token?: string | null): Promise<EventLogItem[]> {
    if (demo) {
      return delay(pickData(true, baseEvents, demoEvents), 200)
    }
    const headers = authHeaders(token)
    const payload = await request<{ items: EventLogItem[] }>("/api/events", { headers })
    return payload.items
  },

  async getStatusHealth(demo: boolean, token?: string | null) {
    if (demo) {
      const smsReady = demoTenantTemplate.allow_web_send
      const digestEnabled = demoNotificationPreferences.weekly_digest
      const pendingSubscription = demoSubscriptionRecords.some((record) => record.status === "pending")
      const cancelScheduled = demoSubscriptionRecords.some((record) => record.cancel_at_period_end)
      const notificationsState = digestEnabled ? "ok" : "degraded"
      const subscriptionState = pendingSubscription || cancelScheduled ? "degraded" : "ok"
      const subscriptionMessage = pendingSubscription
        ? "Pending checkout awaiting customer"
        : cancelScheduled
          ? "Renewal scheduled to cancel"
          : null
      const bannerIssues: string[] = []
      if (!smsReady) {
        bannerIssues.push("SMS rail paused in demo mode")
      }
      if (!digestEnabled) {
        bannerIssues.push("Weekly digest disabled in demo")
      }
      if (subscriptionMessage) {
        bannerIssues.push(subscriptionMessage)
      }
      const bannerState = bannerIssues.length ? "degraded" : "ok"
      const bannerMessage = bannerIssues[0] ?? null
      const twilioStatus = { state: smsReady ? "ok" : "degraded", message: smsReady ? null : "Demo Twilio outage" }
      const stripeStatus = { state: "ok", message: null }
      const notificationsStatus = {
        state: notificationsState,
        message: notificationsState === "ok" ? null : "Weekly digest disabled in demo",
      }
      const subscriptionsStatus = {
        state: subscriptionState,
        message: subscriptionMessage,
      }
      const details = [] as Array<{ label: string; rail: string; state: string; message: string | null }>
      if (twilioStatus.state === "degraded") {
        details.push({ label: "Twilio", rail: "twilio", state: "degraded", message: twilioStatus.message })
      }
      if (notificationsStatus.state === "degraded") {
        details.push({ label: "Notifications", rail: "notifications", state: "degraded", message: notificationsStatus.message })
      }
      if (subscriptionsStatus.state === "degraded") {
        details.push({ label: "Subscriptions", rail: "subscriptions", state: "degraded", message: subscriptionsStatus.message })
      }
      const rails = details.map((detail) => detail.label)
      const incidentFragment = details.find((detail) => detail.message)?.message ?? null
      const detailFragment = incidentFragment ? ` (${incidentFragment})` : ""
      const joinedRails = rails.join(" & ")
      const defaultCopy = rails.length ? `${joinedRails} degraded${detailFragment} — automated fallbacks engaged.` : null
      const personaCopy = {
        demon: rails.length
          ? `Demon fallback — ${joinedRails} degraded${detailFragment}. I'll queue retries and reroute via email until it recovers.`
          : null,
        fairy: rails.length
          ? `Fairy fallback — ${joinedRails} is shimmering offbeat${detailFragment}. I'll ease messages through email while we retry the carrier.`
          : null,
      }
      const resolvedBannerMessage = bannerState === "degraded" ? bannerMessage ?? defaultCopy : bannerMessage ?? null
      return delay(
        {
          twilio: twilioStatus,
          stripe: stripeStatus,
          notifications: notificationsStatus,
          subscriptions: subscriptionsStatus,
          degraded_banner: {
            state: bannerState,
            message: resolvedBannerMessage,
            rails,
            details,
            default_copy: defaultCopy,
            persona_copy: personaCopy,
          },
        },
        120,
      )
    }
    const headers = authHeaders(token)
    return request("/api/status/health", { headers })
  },

  async getNotificationPreferences(demo: boolean, token?: string | null) {
    if (demo) {
      syncDemoDigestCard()
      const channelUpdates = Object.values(demoNotificationPreferences.channels)
        .map((entry) => entry.updated_at)
        .filter(Boolean) as string[]
      const candidates = [demoNotificationPreferences.weekly_digest_updated_at, ...channelUpdates].filter(Boolean) as string[]
      const lastUpdated = candidates.length ? candidates.sort().at(-1) ?? null : null
      const response = {
        weekly_digest: demoNotificationPreferences.weekly_digest,
        weekly_digest_updated_at: demoNotificationPreferences.weekly_digest_updated_at,
        channels: demoNotificationPreferences.channels,
        last_updated_at: lastUpdated,
      }
      return delay(response, 120)
    }
    const headers = authHeaders(token)
    return request("/api/notifications/preferences", { headers })
  },

  async updateNotificationPreferences(
    demo: boolean,
    payload: {
      weekly_digest: boolean
      channels: Record<string, { digest_enabled: boolean; quiet_hours?: Record<string, string | null> | null }>
    },
    token?: string | null,
  ) {
    if (demo) {
      demoNotificationPreferences.weekly_digest = payload.weekly_digest
      demoNotificationPreferences.weekly_digest_updated_at = new Date().toISOString()
      for (const [channel, data] of Object.entries(payload.channels)) {
        demoNotificationPreferences.channels[channel] = {
          digest_enabled: data.digest_enabled,
          quiet_hours: data.quiet_hours ?? null,
          updated_at: new Date().toISOString(),
        }
      }
      syncDemoDigestCard()
      const lastUpdated = demoNotificationPreferences.weekly_digest_updated_at
      return delay(
        {
          weekly_digest: demoNotificationPreferences.weekly_digest,
          weekly_digest_updated_at: demoNotificationPreferences.weekly_digest_updated_at,
          channels: demoNotificationPreferences.channels,
          last_updated_at: lastUpdated,
        },
        120,
      )
    }
    const headers = authHeaders(token)
    return request("/api/notifications/preferences", {
      method: "PUT",
      headers,
      body: JSON.stringify(payload),
    })
  },

  async postAction(
    demo: boolean,
    endpoint: "resend_pay_link" | "issue_balance_now",
    payload: Record<string, unknown>,
    token?: string | null,
  ): Promise<{ ok: true }> {
    if (demo) {
      return delay({ ok: true }, 120)
    }
    const actionPath = endpoint === "issue_balance_now" ? "issue-balance" : "resend-pay-link"
    await request(`/actions/${actionPath}`, {
      method: "POST",
      headers: authHeaders(token),
      body: JSON.stringify(payload),
    })
    return { ok: true }
  },

  async refreshContext(
    demo: boolean,
    payload: Record<string, unknown>,
    token?: string | null,
  ): Promise<{ ok: true }> {
    if (demo) {
      return delay({ ok: true }, 120)
    }
    await request("/actions/refresh-context", {
      method: "POST",
      headers: authHeaders(token),
      body: JSON.stringify(payload),
    })
    return { ok: true }
  },

  async getSubscription(demo: boolean, token?: string | null) {
    if (demo) {
      syncDemoSubscriptionSummary()
      const snapshot = clone({
        ...demoSubscription,
        cards: demoSubscription.cards?.map((card) =>
          card.id === "weekly_digest"
            ? {
                ...card,
                status: demoNotificationPreferences.weekly_digest ? "enabled" : "disabled",
                updated_at: demoNotificationPreferences.weekly_digest_updated_at,
              }
            : card,
        ),
      })
      return delay(snapshot, 160)
    }
    return request("/api/subscriptions", { headers: authHeaders(token) })
  },

  async upgradeSubscription(
    demo: boolean,
    payload: { priceId?: string; quantity?: number },
    token?: string | null,
  ) {
    if (demo) {
      const pending = [...demoSubscriptionRecords]
        .filter((record) => record.status === "pending" && record.checkout_session_id)
        .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())[0]
      let current: DemoSubscriptionRecord | null = pending ?? latestDemoSubscriptionRecordForContact(demoLastSubscriptionContactId)
      if (!current) {
        current = latestDemoSubscriptionRecord()
      }
      if (current) {
        current.status = "active"
        current.cancel_at_period_end = false
        current.updated_at = new Date().toISOString()
        demoLastSubscriptionContactId = current.contact_id ?? demoLastSubscriptionContactId
        if (payload.quantity && payload.quantity > 0) {
          current.amount_cents = Math.max(1000, current.amount_cents * payload.quantity)
        }
      }
      syncDemoSubscriptionSummary()
      return delay(clone(demoSubscription), 140)
    }
    return request("/api/subscriptions/upgrade", {
      method: "POST",
      headers: authHeaders(token),
      body: JSON.stringify({ price_id: payload.priceId, quantity: payload.quantity }),
    })
  },

  async cancelSubscription(demo: boolean, payload: { atPeriodEnd: boolean }, token?: string | null) {
    if (demo) {
      const target = latestDemoSubscriptionRecordForContact(demoLastSubscriptionContactId)
      const current = target ?? latestDemoSubscriptionRecord()
      if (current) {
        current.cancel_at_period_end = payload.atPeriodEnd
        current.status = payload.atPeriodEnd ? "canceling" : "active"
        current.updated_at = new Date().toISOString()
        demoLastSubscriptionContactId = current.contact_id ?? demoLastSubscriptionContactId
      }
      syncDemoSubscriptionSummary()
      return delay(clone(demoSubscription), 140)
    }
    return request("/api/subscriptions/cancel", {
      method: "POST",
      headers: authHeaders(token),
      body: JSON.stringify({ at_period_end: payload.atPeriodEnd }),
    })
  },
  async createSubscriptionCheckout(
    demo: boolean,
    payload: { contactId: string; amountCents: number; cadence: string; currency?: string; email?: string; description?: string },
    token?: string | null,
  ) {
    if (demo) {
      const now = new Date()
      const checkoutSessionId = `cs_demo_${now.getTime()}`
      const subscriptionId = `sub_demo_${now.getTime()}`
      const currency = (payload.currency ?? defaultSubscriptionDefaults.currency ?? "USD").toUpperCase()
      const record: DemoSubscriptionRecord = {
        id: subscriptionId,
        contact_id: payload.contactId,
        contact_name: resolveContactName(payload.contactId),
        status: "pending",
        cadence: payload.cadence,
        amount_cents: payload.amountCents,
        currency,
        cancel_at_period_end: false,
        current_period_end: null,
        stripe_subscription_id: null,
        checkout_session_id: checkoutSessionId,
        checkout_url: `https://billing.stripe.com/p/test_${checkoutSessionId}`,
        description: payload.description ?? `${payload.cadence} cadence for ${resolveContactName(payload.contactId)}`,
        created_at: now.toISOString(),
        updated_at: now.toISOString(),
        metadata: {
          checkout_session_id: checkoutSessionId,
          amount_cents: payload.amountCents,
          cadence: payload.cadence,
          currency,
          email: payload.email ?? null,
        },
      }
      demoSubscriptionRecords = demoSubscriptionRecords.filter(
        (existing) => !(existing.contact_id === record.contact_id && existing.status === "pending"),
      )
      demoSubscriptionRecords.push(record)
      demoLastSubscriptionContactId = record.contact_id ?? demoLastSubscriptionContactId
      syncDemoSubscriptionSummary()
      const subscriptionRow = {
        id: subscriptionId,
        tenant_id: 1,
        contact_id: parseContactNumericId(record.contact_id),
        stripe_subscription_id: record.stripe_subscription_id,
        stripe_price_id: null,
        cadence: record.cadence,
        amount_cents: record.amount_cents,
        status: record.status,
        metadata: record.metadata ?? {
          checkout_session_id: checkoutSessionId,
          checkout_url: record.checkout_url,
          description: record.description,
        },
        defaults: {
          cadence: record.cadence,
          amount_cents: record.amount_cents,
          currency,
        },
        created_at: record.created_at,
        updated_at: record.updated_at,
      }
      const response = {
        subscription: subscriptionRow,
        checkout_session: {
          id: checkoutSessionId,
          url: record.checkout_url,
          status: "open",
          expires_at: Math.floor(Date.now() / 1000) + 1800,
        },
      }
      return delay(response, 160)
    }
    const numericContactId = parseContactNumericId(payload.contactId)
    if (numericContactId == null) {
      throw new Error("Invalid contact id for checkout")
    }
    return request("/api/client-subscriptions/checkout", {
      method: "POST",
      headers: authHeaders(token),
      body: JSON.stringify({
        contact_id: numericContactId,
        amount_cents: payload.amountCents,
        cadence: payload.cadence,
        currency: payload.currency,
        email: payload.email,
        description: payload.description,
      }),
    })
  },

  async updateTenantProfile(
    demo: boolean,
    payload: { name: string; support_number?: string | null; brand_logo_url?: string | null; allow_web_send: boolean; notifications?: Record<string, boolean> | null },
    token?: string | null,
  ) {
    if (demo) {
      demoTenantTemplate.name = payload.name
      demoTenantTemplate.support_number = payload.support_number ?? undefined
      demoTenantTemplate.brand_logo_url = payload.brand_logo_url ?? undefined
      demoTenantTemplate.allow_web_send = payload.allow_web_send
      if (payload.notifications) {
        demoTenantTemplate.preferences = {
          ...(demoTenantTemplate.preferences ?? {}),
          notifications: { ...payload.notifications },
        }
      }
      return delay({ ...payload, id: 0 }, 140)
    }
    const headers = authHeaders(token)
    return request("/api/tenant", {
      method: "POST",
      headers,
      body: JSON.stringify({
        name: payload.name,
        support_number: payload.support_number,
        brand_logo_url: payload.brand_logo_url,
        allow_web_send: payload.allow_web_send,
        notifications: payload.notifications ?? undefined,
      }),
    })
  },

  async getQuietHours(demo: boolean, token?: string | null) {
    if (demo) {
      return delay({ ...demoQuietHoursState }, 120)
    }
    return request("/api/quiet-hours", { headers: authHeaders(token) })
  },

  async setQuietHours(
    demo: boolean,
    payload: { enabled: boolean; start_time?: string | null; end_time?: string | null; timezone?: string | null },
    token?: string | null,
  ) {
    if (demo) {
      if (!payload.enabled) {
        demoQuietHoursState = { enabled: false, start_time: null, end_time: null, timezone: demoQuietHoursState.timezone }
      } else {
        demoQuietHoursState = {
          enabled: true,
          start_time: payload.start_time ?? demoQuietHoursState.start_time,
          end_time: payload.end_time ?? demoQuietHoursState.end_time,
          timezone: payload.timezone ?? demoQuietHoursState.timezone,
        }
      }
      return delay({ ...demoQuietHoursState }, 120)
    }
    return request("/api/quiet-hours", {
      method: "POST",
      headers: authHeaders(token),
      body: JSON.stringify(payload),
    })
  },

  async disconnectStripe(demo: boolean, token?: string | null) {
    if (demo) {
      return delay({ ok: true }, 120)
    }
    await request("/stripe/oauth", { method: "DELETE", headers: authHeaders(token) })
    return { ok: true }
  },

  async getSenderStatus(demo: boolean, token?: string | null) {
    if (demo) {
      return delay(clone(demoSenderStatus), 150)
    }
    return request<SenderStatusPayload>("/api/sender/status", { headers: authHeaders(token) })
  },

  async ensureSenderReady(
    demo: boolean,
    payload: { region?: string; request_whatsapp?: boolean },
    token?: string | null,
  ): Promise<SenderStatusPayload> {
    if (demo) {
      const smsIdentifier = demoSenderStatus.sms?.sender_identifier ?? "+15559871234"
      const updatedSms: SenderChannelStatus = {
        ...(demoSenderStatus.sms || {}),
        status: "ready",
        sender_identifier: smsIdentifier,
        pending_actions: [],
      }
      let updatedWhatsapp: SenderChannelStatus | null = demoSenderStatus.whatsapp || null
      if (payload.request_whatsapp) {
        updatedWhatsapp = {
          ...(demoSenderStatus.whatsapp || {}),
          status: "ready",
          sender_identifier: `whatsapp:${smsIdentifier.replace(/^\+/, "+")}`,
          pending_actions: [],
        }
      }
      demoSenderStatus = {
        ...demoSenderStatus,
        sms: updatedSms,
        whatsapp: updatedWhatsapp,
      }
      return delay(clone(demoSenderStatus), 150)
    }
    return request<SenderStatusPayload>("/api/sender/provision", {
      method: "POST",
      headers: authHeaders(token),
      body: JSON.stringify(payload),
    })
  },

  async getPublicInvoice(token: string, demo: boolean) {
    if (demo) {
      return delay(
        {
          token,
          status: "open",
          amount_cents: 120000,
          currency: "USD",
          created_at: new Date().toISOString(),
          due_date: new Date(Date.now() + 7 * 86400000).toISOString(),
          hosted_invoice_url: "https://pay.stripe.com/test_invoice",
          customer: {
            name: "D***n",
            email: "d***@example.com",
          },
          description: "Demo invoice",
          is_deposit: false,
          stripe_invoice_id: "in_demo",
        },
        140,
      )
    }
    return request(`/api/public/invoice/${token}`)
  },

  async getAdminDeadLetters(
    demo: boolean,
    params: { limit?: number; cursor?: number | null } = {},
    token?: string | null,
  ) {
    if (demo) {
      return delay({ items: demoDeadLetters, next_cursor: null }, 140)
    }
    const search = new URLSearchParams()
    if (params.limit) search.set("limit", String(params.limit))
    if (params.cursor) search.set("cursor", String(params.cursor))
    const suffix = search.toString() ? `?${search}` : ""
    return request(`/api/admin/dead-letters${suffix}`, { headers: authHeaders(token) })
  },

  async requeueDeadLetter(demo: boolean, deadLetterId: number, token?: string | null) {
    if (demo) {
      return delay({ status: "queued", dead_letter_id: deadLetterId, schedule_id: 42 }, 140)
    }
    return request(`/api/admin/dead-letters/${deadLetterId}/requeue`, {
      method: "POST",
      headers: authHeaders(token),
    })
  },

  async getAdminWebhooks(
    demo: boolean,
    params: { limit?: number; cursor?: number | null } = {},
    token?: string | null,
  ) {
    if (demo) {
      return delay({ items: demoWebhookEvents, next_cursor: null }, 120)
    }
    const search = new URLSearchParams()
    if (params.limit) search.set("limit", String(params.limit))
    if (params.cursor) search.set("cursor", String(params.cursor))
    const suffix = search.toString() ? `?${search}` : ""
    return request(`/api/admin/webhooks${suffix}`, { headers: authHeaders(token) })
  },

  async getAdminTenants(
    demo: boolean,
    params: { limit?: number; cursor?: number | null } = {},
    token?: string | null,
  ) {
    if (demo) {
      return delay({ items: demoAdminTenants, next_cursor: null }, 120)
    }
    const search = new URLSearchParams()
    if (params.limit) search.set("limit", String(params.limit))
    if (params.cursor) search.set("cursor", String(params.cursor))
    const suffix = search.toString() ? `?${search}` : ""
    return request(`/api/admin/tenants${suffix}`, { headers: authHeaders(token) })
  },

  __resetDemoSubscriptions() {
    demoSubscriptionRecords = buildInitialDemoSubscriptionRecords()
    demoLastSubscriptionContactId = demoSubscriptionRecords[0]?.contact_id ?? null
    syncDemoSubscriptionSummary()
  },
}

;(globalThis as typeof globalThis & { __mockClient?: typeof mockClient }).__mockClient = mockClient

export function integrationLabel(name: IntegrationName) {
  switch (name) {
    case "stripe":
      return "Stripe"
    case "xero":
      return "Xero"
    case "quickbooks":
      return "QuickBooks"
    case "wave":
      return "Wave"
    default:
      return name
  }
}

if (typeof window !== "undefined" && (window as unknown as { Cypress?: unknown }).Cypress) {
  ;(window as typeof window & { __mockClient?: typeof mockClient }).__mockClient = mockClient
}
